import { type AggregateAttributes } from "@src/appV2/redesign/Workplace/Review/api/useGetMultipleChoiceAggregates";
import uniqBy from "lodash/uniqBy";

import { QuestionTag } from "../Review/types";
import { WorkplaceHighlightType } from "./constants";
import {
  getWorkplaceHighlightLabel,
  type WorkplaceHighlightItem,
} from "./getWorkplaceHighlightLabel";
import type { WorkplaceHighlight } from "./types";

enum SuppliesAnswer {
  WELL_SUPPLIED = "Most workers have all the supplies they need",
}

enum ChartingSoftware {
  POINT_CLICK_CARE = "PointClickCare",
  MATRIX_CARE = "MatrixCare",
  AMERICAN_HEALTH_TECH = "American HealthTech",
  CARE_VOYANT = "CareVoyant",
}

const answerByChartingSoftware: Record<ChartingSoftware, string> = {
  [ChartingSoftware.POINT_CLICK_CARE]: "The charting software is PointClickCare",
  [ChartingSoftware.MATRIX_CARE]: "The charting software is MatrixCare",
  [ChartingSoftware.AMERICAN_HEALTH_TECH]: "The charting software is American HealthTech",
  [ChartingSoftware.CARE_VOYANT]: "The charting software is CareVoyant",
};

enum PatientsPerNursePeriod {
  AM = "AM",
  PM = "PM",
  NOC = "NOC",
}

interface PatientsPerNurse {
  period: PatientsPerNursePeriod;
  value: string;
}

interface PatientsPerNurseWithQualification extends PatientsPerNurse {
  qualification: string;
}

const highlightTypeByPeriod: Record<
  PatientsPerNursePeriod,
  Extract<
    WorkplaceHighlightType,
    | WorkplaceHighlightType.AM_PATIENTS_PER_WORKER
    | WorkplaceHighlightType.PM_PATIENTS_PER_WORKER
    | WorkplaceHighlightType.NOC_PATIENTS_PER_WORKER
  >
> = {
  [PatientsPerNursePeriod.AM]: WorkplaceHighlightType.AM_PATIENTS_PER_WORKER,
  [PatientsPerNursePeriod.PM]: WorkplaceHighlightType.PM_PATIENTS_PER_WORKER,
  [PatientsPerNursePeriod.NOC]: WorkplaceHighlightType.NOC_PATIENTS_PER_WORKER,
};

const answerPatternByPatientsPerNursePeriod: Record<PatientsPerNursePeriod, string> = {
  [PatientsPerNursePeriod.AM]: "AM: There were __VALUE__ patients per nurse",
  [PatientsPerNursePeriod.PM]: "PM: There were __VALUE__ patients per nurse",
  [PatientsPerNursePeriod.NOC]: "NOC: There were __VALUE__ patients per nurse",
};

const getPatientsPerNurseByPeriodFromAnswer = (answer: string): PatientsPerNurse[] => {
  const patientsPerNurseRatios: PatientsPerNurse[] = [];

  // Split by comma to handle combined ratios
  const segments = answer.split(",").map((segment) => segment.trim());

  segments.forEach((segment) => {
    Object.values(PatientsPerNursePeriod).forEach((period) => {
      const answerPattern = answerPatternByPatientsPerNursePeriod[period];
      const [startOfAnswerPattern, endOfAnswerPattern] = answerPattern.split("__VALUE__");

      if (segment.startsWith(startOfAnswerPattern)) {
        // For "AM: There were 8 patients per nurse", the value is "8"
        const value = segment
          .replace(startOfAnswerPattern, "")
          .replace(endOfAnswerPattern, "")
          .trim();

        patientsPerNurseRatios.push({
          period,
          value,
        });
      }
    });
  });

  return patientsPerNurseRatios;
};

const getPatientsPerNurseByQualificationAndPeriodFromAnswer = (
  answer: string,
  qualifications: string[]
): PatientsPerNurseWithQualification[] => {
  const patientsPerNurseRatios: PatientsPerNurseWithQualification[] = [];

  const segments = answer.split(",").map((segment) => segment.trim());
  for (const segment of segments) {
    for (const qualification of qualifications) {
      for (const period of Object.values(PatientsPerNursePeriod)) {
        const answerPattern = `${qualification} - ${period}: There were __VALUE__ patients per nurse`;
        const [startOfAnswerPattern, endOfAnswerPattern] = answerPattern.split("__VALUE__");

        if (segment.startsWith(startOfAnswerPattern)) {
          /// For "CNA - AM: There were 8 patients per nurse", the value is "8"
          const value = segment
            .replace(startOfAnswerPattern, "")
            .replace(endOfAnswerPattern, "")
            .trim();

          patientsPerNurseRatios.push({
            qualification,
            period,
            value,
          });
        }
      }
    }
  }

  return patientsPerNurseRatios;
};

export interface AggregatedReviews {
  tags: QuestionTag[];
  text: AggregateAttributes["text"];
  variations?: Array<
    | `${QuestionTag.PATIENTS_PER_NURSE}:SHIFT_PERIOD`
    | `${QuestionTag.PATIENTS_PER_NURSE}:QUALIFICATION_AND_SHIFT_PERIOD`
  >;
}

interface BuildWorkplaceReviewHighlightsProps {
  aggregatedReviews: AggregatedReviews[];
  /**
   * Qualifications to use for the patient ratio highlights.
   *
   * If not provided, patient ratio highlights will be based on the shift period.
   */
  qualifications: string[];
  excludeList?: QuestionTag[];
  mandatoryBreakDurationInMinutes: number;
}

export function buildWorkplaceReviewHighlights({
  aggregatedReviews,
  qualifications,
  excludeList,
  mandatoryBreakDurationInMinutes,
}: BuildWorkplaceReviewHighlightsProps): WorkplaceHighlight[] {
  const highlightItems: WorkplaceHighlightItem[] = [];

  if (aggregatedReviews.length === 0) {
    return [];
  }

  const findReviewsForTag = (questionTag: QuestionTag) => {
    if (excludeList?.includes(questionTag)) {
      return [];
    }

    return aggregatedReviews.filter((aggregatedReview) =>
      aggregatedReview.tags.includes(questionTag)
    );
  };

  const findChartingSoftwareFromReviews = (): ChartingSoftware | undefined => {
    const chartingSoftwareReview = findReviewsForTag(QuestionTag.SOFTWARE)?.[0];
    if (!chartingSoftwareReview) {
      return undefined;
    }

    return Object.values(ChartingSoftware).find(
      (value) => answerByChartingSoftware[value] === chartingSoftwareReview.text
    );
  };

  const patientsPerNurseReviews = findReviewsForTag(QuestionTag.PATIENTS_PER_NURSE);
  const isQualificationAndShiftPeriodVariationEnabled = patientsPerNurseReviews.every((review) =>
    review.variations?.includes(`${QuestionTag.PATIENTS_PER_NURSE}:QUALIFICATION_AND_SHIFT_PERIOD`)
  );
  // We can build the highlight for patient per nurse
  if (patientsPerNurseReviews.length > 0) {
    if (isQualificationAndShiftPeriodVariationEnabled) {
      // In case the same qualification and period is mentioned multiple times, we want to deduplicate. It will use the last found value.
      const patientsPerNurseByQualificationAndPeriodRatios = uniqBy(
        patientsPerNurseReviews.flatMap((review) =>
          getPatientsPerNurseByQualificationAndPeriodFromAnswer(review.text, qualifications)
        ),
        (item) => `${item.qualification}:${item.period}`
      );

      // Group period ratios by qualification since we want to consolidate them into a single highlight.
      const groupedByQualification = patientsPerNurseByQualificationAndPeriodRatios.reduce<
        Record<string, PatientsPerNurseWithQualification[]>
      >((accumulator, item) => {
        accumulator[item.qualification] ||= [];
        accumulator[item.qualification].push(item);
        return accumulator;
      }, {});

      for (const [qualification, patientsPerNurseRatios] of Object.entries(
        groupedByQualification
      )) {
        highlightItems.push({
          type: WorkplaceHighlightType.QUALIFICATION_PATIENTS_PER_WORKER,
          value: {
            qualification,
            ratios: patientsPerNurseRatios,
          },
        });
      }
    } else {
      // In case the same period is mentioned multiple times, we want to deduplicate. It will use the last found value.
      const patientsPerNurseByPeriodRatios = uniqBy(
        patientsPerNurseReviews.flatMap((review) =>
          getPatientsPerNurseByPeriodFromAnswer(review.text)
        ),
        "period"
      );

      patientsPerNurseByPeriodRatios.forEach((patientsPerNurseRatio) => {
        const highlightType = highlightTypeByPeriod[patientsPerNurseRatio.period];
        highlightItems.push({
          type: highlightType,
          value: patientsPerNurseRatio.value,
        });
      });
    }
  }

  const suppliesReview = findReviewsForTag(QuestionTag.SUPPLIES)?.[0];
  if (suppliesReview?.text === SuppliesAnswer.WELL_SUPPLIED) {
    highlightItems.push({
      type: WorkplaceHighlightType.WELL_SUPPLIED,
    });
  }

  const chartingSoftware = findChartingSoftwareFromReviews();
  if (chartingSoftware) {
    highlightItems.push({
      type: WorkplaceHighlightType.CHARTING_SOFTWARE,
      value: chartingSoftware,
    });
  }

  return highlightItems.map((highlightItem) => ({
    type: highlightItem.type,
    label: getWorkplaceHighlightLabel(highlightItem, mandatoryBreakDurationInMinutes),
  }));
}
